import { Component, OnDestroy, OnInit, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { OnlineStatusType } from 'ngx-online-status';

import { Observable, Subject, timer } from 'rxjs';
import { auditTime, map, share, switchMap, take, takeUntil } from 'rxjs/operators';
import { SettingsForm } from 'src/app/modules/crm/settings/components/settings/settings.interface';
import { SettingsService } from 'src/app/services/settings.service';
import { environment } from '../../../../environments/environment';
import { NotificationPopupComponent } from '../../../dialogs/notification-popup/notification-popup.component';
import {
  DEFAULT_ERROR,
  DEFAULT_PROFILE_ICON,
  ERROR_MESSAGES,
  FUNNEL_TYPE,
  activeTranslations,
  PERMISSIONS,
  quotes,
} from '../../../enum/constants';
import { differenceDate, formatFullName } from '../../../helpers';
import { IError } from '../../../interfaces';
import { AudioService } from '../../../services/audio.service';
import { AuthService } from '../../../services/auth.service';
import { BaseService } from '../../../services/base.service';
import { ChatService } from '../../../services/chat.service';
import { CompanyService } from '../../../services/company.service';
import { DialogService } from '../../../services/dialog.service';
import { ModalService } from '../../../services/modal.service';
import { NotificationService } from '../../../services/notifications.service';
import { RightMenuService } from '../../../services/right-menu.service';
import { SidebarService } from '../../../services/sidebar.service';
import { StackedSidebarService } from '../../../services/stacked-sidebar.service';
import { TariffService } from '../../../services/tariff.service';
import { LanguageService } from '../../../core/i18n';
import { SidebarType } from '../stacked-sidebar/stacked-sidebar.interface';
import { Company, CompanyForm } from './header.interface';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class HeaderComponent implements OnInit, OnDestroy {
  protected readonly OnlineStatusType = OnlineStatusType;

  public languages = [
    { name: 'UA', value: 'ua' },
    { name: 'EN', value: 'en' },
  ];

  public fullLanguages = [
    { name: 'Українська', value: 'ua' },
    { name: 'English', value: 'en' },
  ];
  private readonly destroy$ = new Subject<void>();

  private readonly chatIdQuery: string = this.activatedRoute.snapshot.queryParams['chat'];
  private readonly dealIdQuery: string = this.activatedRoute.snapshot.queryParams['deal'];
  private readonly leadIdQuery: string = this.activatedRoute.snapshot.queryParams['lead'];
  private readonly cardIdQuery: string = this.activatedRoute.snapshot.queryParams['card'];

  public company!: Company;
  public companyName: string = '';
  public companySubdomain: string = '';

  public warn: boolean = false;
  public isHasChatPer: boolean = false;
  public isHasTeamPer: boolean = false;
  public isHasSettingsPer: boolean = false;

  public formSubmitted: boolean = false;
  public companyForm!: FormGroup<CompanyForm>;
  public settingsForm!: FormGroup<SettingsForm>;

  public rxDate = new Date();
  public trialDays!: number;

  public currentQuote = this.getRandomQuote();
  public notificationLoader: boolean = false;
  public activeTab: 'inbox' | 'archived' = 'inbox';
  public notifications: any[] = []; // тільки непрочитані
  public allNotifications: any[] = []; // всі отримані
  get archivedNotifications(): any[] {
    return this.allNotifications.filter((n: any) => n.is_read);
  }
  get unreadCount(): number {
    return this.notifications.length;
  }

  trackById = (_: number, n: any) => n?.id ?? n?.created_at ?? _;

  constructor(
    public readonly authService: AuthService,
    public readonly baseService: BaseService,
    public readonly sidebarService: SidebarService,
    public readonly companyService: CompanyService,
    public readonly rightMenuService: RightMenuService,
    private readonly stackedSidebarService: StackedSidebarService,
    public readonly chatService: ChatService,
    public readonly modalService: ModalService,
    public readonly notificationService: NotificationService,
    public readonly activatedRoute: ActivatedRoute,
    public readonly router: Router,
    public readonly dialogService: DialogService,
    public readonly tariffService: TariffService,
    public readonly audioService: AudioService,
    public readonly settingsService: SettingsService,
    private translate: TranslateService,
    private readonly languageService: LanguageService,
  ) {}

  ngOnInit() {
    this.isHasChatPer = this.baseService.isHasPermission(PERMISSIONS.CHATS_ACCESS);
    this.isHasTeamPer = this.baseService.isHasPermission(PERMISSIONS.TEAM_ACCESS);
    this.isHasSettingsPer = this.baseService.isHasPermission(PERMISSIONS.SETTINGS_ACCESS);

    this.company = this.baseService.getCompanyObj();
    this.companyName = this.company.name;
    this.companySubdomain = this.company.subdomain;
    this.initForm();

    this.loadNotifications();
    this.initNotificationComponent();
    this.notificationService.getCountNotification().pipe(takeUntil(this.destroy$)).subscribe();

    this.notificationService
      .getLastNotification()
      .pipe(takeUntil(this.destroy$), auditTime(120))
      .subscribe({
        next: (all: unknown) => {
          const list = (all as any[]) ?? [];
          this.allNotifications = list;
          this.notifications = list.filter((n) => !n.is_read);
        },
        error: () => {
          /* no-op або логування */
        },
      });

    timer(0, 1000)
      .pipe(
        takeUntil(this.destroy$),
        map(() => new Date()),
        share(),
      )
      .subscribe((date) => {
        this.rxDate = date;
      });

    if (this.chatIdQuery || typeof this.chatIdQuery === 'string') {
      if (!this.baseService.isHasPermission(PERMISSIONS.CHATS_ACCESS)) this.router.navigate(['/crm/support']);
      else this.openChat();
    }

    if (this.dealIdQuery || this.leadIdQuery || this.cardIdQuery) {
      if (!this.baseService.isHasPermission(PERMISSIONS.CRM_ACCESS)) this.router.navigate(['/crm/support']);
      this.openCard();
    }

    this.trackerDemo();
  }

  private initForm() {
    this.companyForm = new FormGroup({
      name: new FormControl(this.company?.name, Validators.required),
      subdomain: new FormControl(this.company?.subdomain, Validators.required),
    });

    this.settingsForm = new FormGroup<SettingsForm>({
      is_notification: new FormControl(this.baseService.currentUserInfo.settings.is_notification),
      is_notification_sound: new FormControl(this.baseService.currentUserInfo.settings.is_notification_sound),
      is_message_read: new FormControl(this.baseService.currentUserInfo.settings.is_message_read),
      is_lazy_loading: new FormControl(this.baseService.currentUserInfo.settings.is_lazy_loading),
      language: new FormControl(this.baseService.currentUserInfo.settings.language),
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public logout() {
    this.authService.logout();
  }

  public companies() {
    window.location.href =
      window.location.origin.replace(this.baseService?.currentCompanyInfo?.subdomain, 'auth') + '/companies';
  }

  public getTitle() {
    return this.company?.name;
  }

  private validateSubdomain(input: string) {
    const isLengthValid = input.length >= 1 && input.length <= 10;

    // Перевірка на допустимі символи (букви, цифри та дефіс)
    const hasValidCharacters = /^[a-z0-9\-]+$/.test(input);

    // Перевірка на закінчення дефісом
    const doesNotEndWithDash = !input.endsWith('-');

    // Перевірка на входження в спеціальні домени
    const isNotSpecialDomain = !environment.specialDomains.includes(input);

    // Повертаємо загальний результат перевірок
    return isLengthValid && hasValidCharacters && doesNotEndWithDash && isNotSpecialDomain;
  }

  openChat() {
    this.stackedSidebarService
      .openChats()
      .pipe(take(2))
      .subscribe(() => {
        this.chatService.loadCount();
      });
  }

  openCard() {
    if (this.isBoardV3CardRoute()) {
      this.stackedSidebarService
        .openSidebar(
          {
            type: SidebarType.CardV3,
            width: '90vw',
          },
          {
            card_id: this.cardIdQuery,
            type: this.getBoardV3CardType(),
          },
        )
        .pipe(take(2))
        .subscribe(() => {
          this.chatService.loadCount();
        });
      return;
    }

    this.stackedSidebarService
      .openSidebar(
        {
          type: SidebarType.CardLegacy,
          maxWidth: '90vw',
        },
        {
          card_id: this.dealIdQuery || this.leadIdQuery || this.cardIdQuery,
        },
      )
      .pipe(take(2))
      .subscribe(() => {
        this.chatService.loadCount();
      });
  }

  private isBoardV3CardRoute(): boolean {
    return Boolean(this.cardIdQuery) && this.router.url.startsWith('/crm/v2/');
  }

  private getBoardV3CardType(): FUNNEL_TYPE {
    return this.router.url.startsWith('/crm/v2/leads') ? FUNNEL_TYPE.LEAD : FUNNEL_TYPE.DEAL;
  }

  public submitCompany() {
    this.formSubmitted = true;
    if (this.companyForm.valid) {
      const data = {
        name: this.companyForm.controls.name.value,
      };
      this.companyService
        .updateCompany(data)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            window.location.reload();
          },
          error: (res) => {
            (res.error?.errors || []).forEach((err: IError) => {
              this.notificationService.showError(
                activeTranslations.notifications_texts.error.error,
                DEFAULT_ERROR,
              );
            });
          },
        });
    }
  }

  public submit() {
    this.formSubmitted = true;
    if (this.companyForm.valid) {
      const data = {
        name: this.companyForm.controls.name.value,
        subdomain: this.companyForm.controls.subdomain.value,
      };
      if (!this.validateSubdomain(data.subdomain)) {
        this.companyForm.controls.subdomain.setErrors({});
        return;
      }
      this.companyService
        .updateCompany(data)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            const currentUrl = window.location.href;
            window.location.href = currentUrl.replace(this.companySubdomain, data.subdomain);
          },
          error: (res) => {
            (res.error?.errors || []).forEach((err: IError) => {
              this.notificationService.showError('', ERROR_MESSAGES[err.code]);
            });
          },
        });
    }
  }

  public isEditSubdomain = () => {
    const targetDate = new Date(this.company.created_at);
    const currentDate = new Date();
    const timeDifference = currentDate.getTime() - targetDate.getTime();
    const threeDaysInMilliseconds = 3 * 24 * 60 * 60 * 1000; // 3 дні у мілісекундах
    return !(timeDifference >= threeDaysInMilliseconds);
  };

  public themes = () => {
    this.modalService.openThemes();
  };

  public profile = () => {
    this.router.navigate(['/crm/settings/profile']);
  };

  public settings = () => {
    this.router.navigate(['/crm/settings/general']);
  };

  protected readonly environment = environment;
  protected readonly Date = Date;

  public initNotificationComponent(): void {
    this.notificationLoader = true;
    this.notificationService
      .getUnreadNotification()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (notifications: any[]) => {
          let isOpenPopup = false;
          notifications.forEach((notification) => {
            if (notification.is_popup && !isOpenPopup && notification.x_data?.kind !== 'fiscal_operation') {
              const modalData = {
                title: 'change_price',
                component: NotificationPopupComponent,
                translate: true,
                hideHeader: true,
                disableClose: true,
                // backdropClass: 'my-backdrop',
                // .cdk-overlay-dark-backdrop
                data: {
                  notification,
                },
              };
              isOpenPopup = true;
              this.modalService.openModal(modalData).subscribe();
            }
          });
        },
        error: () => {
          this.notificationLoader = false;
        },
      });
  }

  getRandomQuote() {
    const quote = quotes[Math.floor(Math.random() * quotes.length)];
    this.currentQuote = quote;
    return quote;
  }

  delayedOnReadAll() {
    setTimeout(() => this.onReadAll(), 300);
  }

  delayedSwitchTab(evt: Event, tab: 'inbox' | 'archived') {
    evt.stopPropagation();
    setTimeout(() => {
      this.activeTab = tab;
      this.loadNotifications();
    }, 300);
  }

  public initNotification() {
    this.notificationService.getAllNotification({ is_read: false }).subscribe((notification) => {
      this.notificationService.unreadNotificationList = notification;
    });

    this.notificationService.getAllNotification({ is_read: true }).subscribe((notification) => {
      this.notificationService.readNotificationList = notification;
    });
  }

  public switchTab(tab: 'inbox' | 'archived'): void {
    if (this.activeTab === tab) return;
    this.activeTab = tab;
    this.loadNotifications();
  }
  private loadNotifications(): void {
    if (this.activeTab === 'inbox') {
      (this.notificationService.getUnreadNotification() as unknown as Observable<any[]>)
        .pipe(takeUntil(this.destroy$))
        .subscribe((list) => {
          this.notifications = list ?? [];
        });
    } else {
      (this.notificationService.getLastNotification() as unknown as Observable<any[]>)
        .pipe(takeUntil(this.destroy$))
        .subscribe((all) => {
          this.allNotifications = all ?? [];
        });
    }
  }

  public onReadAll() {
    if (this.activeTab !== 'inbox') return;
    this.notificationService
      .readAllNotification()
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.notificationService.getCountNotification()),
      )
      .subscribe(() => this.loadNotifications());
  }

  startDemo(event: any) {
    if (
      this.baseService.currentCompanyInfo?.tariff === 'free' &&
      !this.baseService.currentCompanyInfo.tariff_updated_at
    ) {
      // event.stopPropagation();
      event.preventDefault();
      const dialogRef = this.dialogService.openConfirm({
        text: 'dialogs.header',
      });

      dialogRef.subscribe((res) => {
        if (res) {
          this.tariffService.activeTrial({}).subscribe();
          window.location.reload();
        }
      });
    }
  }

  trackerDemo() {
    const company = this.baseService.currentCompanyInfo;
    const trialDays = company.trial_days ?? company.config.trial_days;
    const activatedAt = new Date(company.tariff_updated_at).setHours(0, 0, 0, 0);
    const today = new Date().setHours(0, 0, 0, 0);
    const passedDays = Math.round(differenceDate(activatedAt, today));
    if (passedDays > trialDays) return;
    this.trialDays = trialDays - passedDays;
  }

  goToPayment() {
    this.router.navigate(['/crm/payment']);
  }

  /**
   * Перемикання мови без перезавантаження сторінки.
   * Зберігаємо налаштування на бекенді, потім просто просимо LanguageService
   * підмінити словник — TranslationManagerService дотягне вже задіяні модулі,
   * а translate-пайпи перемалюються самі.
   */
  switchLanguage() {
    const language = this.settingsForm.controls.language.value;

    this.settingsService
      .updateSettings(this.settingsForm.getRawValue())
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.languageService.setLanguage(language)),
        switchMap(() => this.baseService.getProfile()),
      )
      .subscribe({
        error: (res) => {
          (res.error?.errors || []).forEach((err: any) => {
            this.notificationService.showError('', err.message);
          });
        },
      });
  }
  public read(item: any) {
    if (item.is_read) return;
    this.notificationService
      .readNotification(item.id)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.notificationService.getCountNotification()),
      )
      .subscribe(() => this.loadNotifications());
  }

  goToDashboard() {
    this.router.navigate(['/crm/dashboard']);
  }

  get getUserName(): string {
    if (this.baseService.user) {
      return formatFullName(this.baseService.user.first_name, this.baseService.user.last_name);
    }
    return 'System';
  }

  get getAvatarUrl(): string {
    if (this.baseService.user.avatar_url) {
      return this.baseService.user.avatar_url;
    }
    return DEFAULT_PROFILE_ICON;
  }

  protected readonly String = String;
}
